import { useMutation } from '@tanstack/react-query';

import { createApiCall } from 'services';

/**
 * VRG-275 — owner confirmation for a replacement tag held in transit.
 *
 * `POST /certificates/:certificateId/replacement/confirm`, authenticated as the
 * owner (the `X-Vera-Token` header is attached automatically by `createApiCall`
 * whenever a stored `accessToken` is present — see `services/index.ts`). The API
 * refuses (404 — deliberately the same shape as "certificate not found", per
 * `ConfirmTagReplacementByOwnerUseCase`'s own comment) anyone whose authenticated
 * identity is not the certificate's owner — that refusal IS the security property
 * this flow exists for (see the task file's DECIDED section) — and separately
 * returns 400 when the certificate has no replacement tag awaiting activation.
 * Callers must branch on the status code rather than surfacing every failure
 * identically (see `pages/TagNotActive`, the only current caller).
 *
 * `throwError: true` so the caller sees the raw axios error (status + body)
 * instead of `createApiCall`'s generic `errorHandler` fallback — mirrors
 * `useAcceptTransfer` / `useCancelTransfer`.
 */
const confirmReplacementReceipt = async (certificateId: string) => {
  await createApiCall({
    method: 'POST',
    url: `/certificates/${certificateId}/replacement/confirm`,
    throwError: true,
  });
};

export const useConfirmReplacementReceipt = () => {
  return useMutation({
    mutationFn: (certificateId: string) => confirmReplacementReceipt(certificateId),
    // Never retry: a 403 (not the owner) must surface immediately, not
    // re-submit the same refused request after the global mutation-retry delay.
    retry: false,
  });
};
